// Snake: tablero 20 × 20 igual para todos, la serpiente acelera con cada punto.
// Se juega al momento: la primera flecha (o deslizar el dedo) crea la partida y la pone en marcha.
// La lógica avanza a pasos fijos y el dibujo se interpola a 60 fps para que el movimiento sea fluido.
// Con sesión iniciada, el servidor repite los giros para calcular la puntuación del ranking.
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeftIcon, QuestionMarkCircleIcon, TrophyIcon, ArrowPathIcon, PauseIcon, PlayIcon } from '@heroicons/react/24/outline';
import GameIcon from '../components/GameIcon';
import Modal from '../components/Modal';
import GameGuide from '../components/GameGuide';
import { newGame, step, stepMs, isOpposite, GRID, type Dir, type SnakeState, type Turn } from '../games/snake/engine';
import { draw, readPalette, type Burst, type Palette } from '../games/snake/draw';
import { useAuthStore } from '../store/authStore';
import { applyXpUpdate, countGamePlay, type XpUpdate } from '../lib/results';
import { API_URL } from '../lib/api';

type Phase = 'ready' | 'loading' | 'playing' | 'paused' | 'over';

interface FinishData {
  score: number;
  personalBest: number;
  newRecord: boolean;
  rank: number | null;
  xp: XpUpdate | null;
}

interface Sim {
  s: SnakeState;
  prev: number[];
  acc: number;          // ms acumulados hacia el siguiente paso
  queue: Dir[];         // giros pendientes (se aplican uno por paso)
  turns: Turn[];        // giros aplicados, para que el servidor repita la partida
  burst: Burst | null;
}

const KEYS: Record<string, Dir> = {
  ArrowUp: 0, ArrowRight: 1, ArrowDown: 2, ArrowLeft: 3,
  w: 0, d: 1, s: 2, a: 3, W: 0, D: 1, S: 2, A: 3,
};
const SWIPE_PX = 14;
const BEST_KEY = 'jugaly-snake-best';

const RULES = [
  { title: 'El objetivo', text: 'Come todas las manzanas que puedas. Cada una suma un punto y alarga la serpiente.' },
  { title: 'Cómo moverse', text: 'Flechas o WASD en el ordenador; en el móvil, desliza el dedo sobre el tablero hacia donde quieras girar.' },
  { title: 'Cada vez más rápido', text: 'La serpiente acelera con cada punto. Si chocas contra la pared o contra ti mismo, se acaba.' },
  { title: 'Pausa', text: 'Espacio o el botón de pausa. Si cambias de pestaña, el juego se pausa solo.' },
  { title: 'El ranking', text: 'Todo el mundo juega el mismo tablero. Cuenta tu mejor puntuación.' },
];

function randomSeed() {
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function readBest(): number {
  try { return Number(localStorage.getItem(BEST_KEY)) || 0; } catch { return 0; }
}

export default function Snake() {
  const { isAuthenticated, user } = useAuthStore();

  const [phase, setPhase] = useState<Phase>('ready');
  const phaseRef = useRef<Phase>('ready');
  const setPhaseBoth = (p: Phase) => { phaseRef.current = p; setPhase(p); };

  const sim = useRef<Sim>({ s: newGame(randomSeed()), prev: [], acc: 0, queue: [], turns: [], burst: null });
  const gameIdRef = useRef<string | null>(null);
  const [score, setScore] = useState(0);
  const [best, setBest] = useState(readBest);
  const [finish, setFinish] = useState<FinishData | null>(null);
  const [saveError, setSaveError] = useState('');
  const [startError, setStartError] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [shake, setShake] = useState(false);

  // ─── Tamaño: el tablero cuadrado ocupa el alto disponible sin hacer scroll ───
  const wrapRef = useRef<HTMLDivElement>(null);
  const [space, setSpace] = useState({ width: 0, height: 0 });
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => {
      const top = el.getBoundingClientRect().top + window.scrollY;
      setSpace({ width: el.clientWidth, height: window.innerHeight - top });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
  }, []);

  const narrow = space.width < 600;
  const pad = narrow ? 12 : 24;
  const below = 44;
  const side = Math.min(space.width - pad, space.height - below - pad);
  const cell = Math.max(12, Math.min(34, Math.floor(side / GRID.cols)));

  // ─── Canvas ───
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const palette = useRef<Palette | null>(null);
  useEffect(() => {
    palette.current = readPalette();
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => { palette.current = readPalette(); };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = GRID.cols * cell * dpr;
    canvas.height = GRID.rows * cell * dpr;
    canvas.style.width = `${GRID.cols * cell}px`;
    canvas.style.height = `${GRID.rows * cell}px`;
    canvas.getContext('2d')?.setTransform(dpr, 0, 0, dpr, 0, 0);
  }, [cell]);

  // ─── Fin de partida ───
  const submit = useCallback(async (gameId: string, turns: Turn[]) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/snake/finish`, { gameId, turns });
      setFinish(data.data);
      applyXpUpdate(data.data.xp);
    } catch (err: any) {
      setSaveError(err.response?.data?.error ?? 'No se ha podido guardar la partida.');
    }
  }, []);

  const endGame = useCallback(() => {
    const { s, turns } = sim.current;
    setPhaseBoth('over');
    setShake(true);
    window.setTimeout(() => setShake(false), 400);
    if (s.score > readBest()) {
      try { localStorage.setItem(BEST_KEY, String(s.score)); } catch { /* sin almacenamiento */ }
      setBest(s.score);
    }
    if (gameIdRef.current) submit(gameIdRef.current, turns.slice());
    window.setTimeout(() => setShowResult(true), 750);
  }, [submit]);

  // ─── Bucle: lógica a pasos fijos + dibujo interpolado en cada fotograma ───
  useEffect(() => {
    let raf = 0;
    let last = performance.now();
    const frame = (now: number) => {
      const dt = Math.min(now - last, 250);
      last = now;
      const g = sim.current;
      const phaseNow = phaseRef.current;

      if (phaseNow === 'playing') {
        g.acc += dt;
        let interval = stepMs(g.s.score);
        while (g.acc >= interval && g.s.alive) {
          g.acc -= interval;
          // Siguiente giro válido de la cola
          let turn: Dir | undefined;
          while (g.queue.length) {
            const d = g.queue.shift()!;
            if (d !== g.s.dir && !isOpposite(d, g.s.dir)) { turn = d; break; }
          }
          if (turn !== undefined) g.turns.push({ k: g.s.tick, d: turn });
          g.prev = g.s.body.slice();
          const before = g.s.score;
          step(g.s, turn);
          if (g.s.score !== before) {
            g.burst = { cell: g.s.body[0], at: now };
            setScore(g.s.score);
            navigator.vibrate?.(8);
          }
          if (!g.s.alive) { g.acc = 0; endGame(); break; }
          interval = stepMs(g.s.score);
        }
      }

      const ctx = canvasRef.current?.getContext('2d');
      if (ctx && palette.current) {
        const alpha = phaseRef.current === 'playing' || phaseRef.current === 'paused' ? Math.min(1, g.acc / stepMs(g.s.score)) : 0;
        draw(ctx, {
          s: g.s,
          prev: g.prev,
          alpha,
          cell,
          showFood: phaseRef.current !== 'ready' && phaseRef.current !== 'loading',
          burst: g.burst,
          now,
          palette: palette.current,
        });
      }
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [cell, endGame]);

  // ─── Empezar, pausar, reiniciar ───
  const reset = () => {
    sim.current = { s: newGame(randomSeed()), prev: [], acc: 0, queue: [], turns: [], burst: null };
    gameIdRef.current = null;
    setScore(0);
    setFinish(null);
    setSaveError('');
    setStartError('');
    setShowResult(false);
    setPhaseBoth('ready');
  };

  // Primera dirección: pide la semilla al servidor (con sesión) y arranca
  const begin = async (first?: Dir) => {
    if (phaseRef.current !== 'ready') return;
    setPhaseBoth('loading');
    let seed = randomSeed();
    gameIdRef.current = null;
    if (isAuthenticated) {
      try {
        const { data } = await axios.post(`${API_URL}/api/snake/start`);
        seed = data.data.seed;
        gameIdRef.current = data.data.gameId;
      } catch {
        setStartError('Sin conexión con el servidor: esta partida no contará para el ranking.');
      }
    }
    sim.current = { s: newGame(seed), prev: [], acc: 0, queue: first !== undefined ? [first] : [], turns: [], burst: null };
    setScore(0);
    setPhaseBoth('playing');
    countGamePlay('snake');
  };

  const pause = () => { if (phaseRef.current === 'playing') setPhaseBoth('paused'); };
  const resume = () => { if (phaseRef.current === 'paused') setPhaseBoth('playing'); };

  // Dirección pedida por teclado o gesto
  const input = (d: Dir) => {
    const p = phaseRef.current;
    if (p === 'ready') { begin(d); return; }
    if (p === 'paused') resume();
    if (p !== 'playing' && p !== 'paused' && p !== 'loading') return;
    const g = sim.current;
    const lastDir = g.queue.length ? g.queue[g.queue.length - 1] : g.s.dir;
    if (d === lastDir || isOpposite(d, lastDir)) return;
    if (g.queue.length < 3) g.queue.push(d);
  };

  // Teclado
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (showResult || showRules) return;
      if (e.key in KEYS) { e.preventDefault(); input(KEYS[e.key]); return; }
      if (e.key === ' ' || e.key === 'p' || e.key === 'P') {
        e.preventDefault();
        if (phaseRef.current === 'playing') pause();
        else if (phaseRef.current === 'paused') resume();
        else if (phaseRef.current === 'ready') begin();
      }
      if (e.key === 'Enter' && phaseRef.current === 'over') reset();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // Pausa automática al cambiar de pestaña
  useEffect(() => {
    const onVis = () => { if (document.hidden) pause(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('blur', pause);
    return () => { document.removeEventListener('visibilitychange', onVis); window.removeEventListener('blur', pause); };
  }, []);

  // Gestos: cada tramo deslizado de más de SWIPE_PX es un giro (se pueden encadenar sin levantar el dedo)
  const swipe = useRef<{ x: number; y: number; moved: boolean } | null>(null);
  const onPointerDown = (e: React.PointerEvent) => {
    swipe.current = { x: e.clientX, y: e.clientY, moved: false };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const sw = swipe.current;
    if (!sw) return;
    const dx = e.clientX - sw.x;
    const dy = e.clientY - sw.y;
    if (Math.max(Math.abs(dx), Math.abs(dy)) < SWIPE_PX) return;
    input(Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? 1 : 3) : (dy > 0 ? 2 : 0));
    swipe.current = { x: e.clientX, y: e.clientY, moved: true };
  };
  const onPointerUp = () => {
    const sw = swipe.current;
    swipe.current = null;
    // Un toque sin deslizar: empieza (recto) o reanuda
    if (sw && !sw.moved) {
      if (phaseRef.current === 'ready') begin();
      else if (phaseRef.current === 'paused') resume();
    }
  };

  const speed = (130 / stepMs(score)).toFixed(1).replace('.', ',');
  const counted = !!gameIdRef.current;

  return (
    <>
    <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-3 sm:pt-5 pb-6">
      {/* Cabecera y marcador en una sola fila para dejar el alto al tablero */}
      <div className="flex items-center gap-1 sm:gap-3 max-w-3xl mx-auto">
        <Link to="/" className="btn-plain -ml-3 h-9 px-2 text-footnote shrink-0" aria-label="Volver a juegos">
          <ChevronLeftIcon className="w-5 h-5" />
        </Link>
        <GameIcon id="snake" size={36} shared />
        <div className="hidden sm:block leading-tight min-w-0 ml-1">
          <h1 className="text-title3 font-semibold text-ink">Snake</h1>
          <p className="text-caption text-ink-2 tabular truncate">{GRID.cols} × {GRID.rows} · acelera con cada punto</p>
        </div>
        <h1 className="sr-only sm:hidden">Snake</h1>

        <div className="flex-1 flex items-center justify-center gap-4 sm:gap-6">
          <span className="flex items-baseline gap-1.5 text-title3 font-semibold text-ink tabular" aria-label={`${score} puntos`} aria-live="polite">
            {score}<span className="text-caption font-medium text-ink-2">pts</span>
          </span>
          <span className="flex items-center gap-1 text-footnote text-ink-2 tabular" aria-label={`Récord: ${Math.max(best, score)}`}>
            <TrophyIcon className="w-4 h-4" /> {Math.max(best, score)}
          </span>
        </div>

        <button
          type="button"
          onClick={() => (phase === 'paused' ? resume() : pause())}
          disabled={phase !== 'playing' && phase !== 'paused'}
          aria-label={phase === 'paused' ? 'Continuar' : 'Pausa'}
          title={phase === 'paused' ? 'Continuar' : 'Pausa'}
          className="w-9 h-9 shrink-0 rounded-full grid place-items-center text-ink-2 hover:text-ink hover:bg-ink/[0.05] transition-colors disabled:opacity-40"
        >
          {phase === 'paused' ? <PlayIcon className="w-5 h-5" /> : <PauseIcon className="w-5 h-5" />}
        </button>
        <button
          type="button"
          onClick={reset}
          disabled={phase === 'ready' || phase === 'loading'}
          aria-label="Nueva partida"
          title="Nueva partida"
          className="w-9 h-9 shrink-0 rounded-full grid place-items-center text-ink-2 hover:text-ink hover:bg-ink/[0.05] transition-colors disabled:opacity-40"
        >
          <ArrowPathIcon className="w-5 h-5" />
        </button>
        <button onClick={() => { pause(); setShowRules(true); }} className="hidden sm:grid w-9 h-9 shrink-0 rounded-full place-items-center text-ink-2 hover:text-ink hover:bg-ink/[0.05] transition-colors" aria-label="Cómo se juega">
          <QuestionMarkCircleIcon className="w-6 h-6" />
        </button>
        <Link to="/ranking?juego=snake" className="w-9 h-9 shrink-0 rounded-full grid place-items-center text-ink-2 hover:text-ink hover:bg-ink/[0.05] transition-colors" aria-label="Ranking">
          <TrophyIcon className="w-6 h-6" />
        </Link>
      </div>

      {/* Tablero */}
      <div ref={wrapRef} className="mt-3 sm:mt-4 -mx-4 sm:mx-0">
        <div
          className={`relative mx-auto w-max rounded-[1.25rem] sm:rounded-3xl bg-surface p-1.5 sm:p-3 shadow-card select-none ${shake ? 'snake-shake' : ''}`}
          style={{ backgroundImage: 'linear-gradient(rgb(var(--game-snake) / 0.06), rgb(var(--game-snake) / 0.06))', touchAction: 'none' }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={() => { swipe.current = null; }}
        >
          <canvas ref={canvasRef} className="block rounded-xl" aria-label="Tablero del Snake" role="img" />

          {phase === 'paused' && (
            <div className="absolute inset-0 grid place-items-center rounded-[inherit] bg-surface/55 backdrop-blur-[2px]">
              <button onClick={resume} className="btn-primary btn-lg">
                <PlayIcon className="w-5 h-5" /> Continuar
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Ayuda y estado bajo el tablero (una línea, cabe en el hueco reservado) */}
      <div className="mt-2.5 min-h-[2rem] max-w-xl mx-auto flex items-center justify-center gap-2 text-center">
        {(phase === 'ready' || phase === 'loading') && (
          <p className="text-footnote text-ink-2">
            {phase === 'loading' ? 'Preparando…' : (
              <>
                <strong className="text-ink font-semibold">
                  <span className="hidden sm:inline">Pulsa una flecha para empezar.</span>
                  <span className="sm:hidden">Desliza el dedo para empezar.</span>
                </strong>{' '}
                {!isAuthenticated && <span className="hidden sm:inline"><Link to="/auth?tab=register" className="text-accent font-medium hover:underline">Crea una cuenta</Link> para entrar en el ranking.</span>}
                {isAuthenticated && user?.isGuest && <span className="hidden sm:inline text-ink-3">Como invitado no sales en el ranking.</span>}
              </>
            )}
          </p>
        )}
        {(phase === 'playing' || phase === 'paused') && (
          startError
            ? <p className="text-caption text-warning">{startError}</p>
            : <p className="text-caption text-ink-3 tabular">
                Velocidad ×{speed}
                <span className="hidden sm:inline"> · Flechas o WASD para girar · Espacio para pausar</span>
                <span className="sm:hidden"> · Desliza para girar</span>
              </p>
        )}
        {phase === 'over' && !showResult && (
          <>
            <button onClick={reset} className="btn-primary h-9">Otra partida</button>
            <button onClick={() => setShowResult(true)} className="btn-secondary h-9">Ver resultado</button>
          </>
        )}
      </div>

      {/* Resultado */}
      <Modal open={showResult && phase === 'over'} onClose={() => setShowResult(false)} labelledBy="snake-result">
        <div className="text-center">
          <div className="mx-auto w-14 h-14 rounded-full grid place-items-center bg-success/10 text-success">
            <TrophyIcon className="w-7 h-7" />
          </div>
          <h2 id="snake-result" className="mt-4 text-title2 font-bold text-ink">
            {sim.current.s.won ? '¡Tablero lleno!' : score === 0 ? 'Fin de la partida' : '¡Buena partida!'}
          </h2>
          <p className="mt-3 text-title1 font-bold text-ink tabular">{score} <span className="text-title3 font-semibold text-ink-2">{score === 1 ? 'punto' : 'puntos'}</span></p>

          {counted && (
            <div className="mt-4 rounded-2xl bg-fill px-4 py-3 text-footnote text-ink-2 min-h-[3rem] flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
              {!finish && !saveError && <span>Guardando…</span>}
              {saveError && <span className="text-danger">{saveError}</span>}
              {finish && (
                <>
                  {finish.newRecord
                    ? <span className="font-semibold text-success">Nuevo récord personal</span>
                    : <span>Tu récord: <strong className="text-ink tabular">{finish.personalBest}</strong></span>}
                  {finish.rank !== null && <span>Puesto <strong className="text-ink tabular">{finish.rank}</strong></span>}
                  {finish.xp && finish.xp.xpAwarded > 0 && <span className="text-accent font-semibold tabular">+{finish.xp.xpAwarded} XP</span>}
                </>
              )}
            </div>
          )}
          {!counted && (
            <p className="mt-4 text-footnote text-ink-2">
              {!isAuthenticated
                ? <><Link to="/auth?tab=register" className="text-accent font-medium hover:underline">Crea una cuenta</Link> para guardar tu puntuación y entrar en el ranking.</>
                : 'Esta partida no se ha guardado.'}
            </p>
          )}
          {counted && user?.isGuest && <p className="mt-2 text-caption text-ink-2">Como invitado no apareces en el ranking.</p>}

          <div className="mt-6 flex flex-col gap-2">
            <button onClick={reset} className="btn-primary w-full">Otra partida</button>
            <Link to="/ranking?juego=snake" className="btn-secondary w-full">Ver ranking</Link>
            <button onClick={() => setShowResult(false)} className="btn-plain w-full">Ver el tablero</button>
          </div>
        </div>
      </Modal>

      {/* Reglas */}
      <Modal open={showRules} onClose={() => setShowRules(false)} labelledBy="snake-rules">
        <h2 id="snake-rules" className="text-title3 font-semibold text-ink">Cómo se juega</h2>
        <dl className="mt-4 flex flex-col gap-3">
          {RULES.map((r) => (
            <div key={r.title}>
              <dt className="text-footnote font-semibold text-ink">{r.title}</dt>
              <dd className="text-footnote text-ink-2">{r.text}</dd>
            </div>
          ))}
        </dl>
        <button onClick={() => setShowRules(false)} className="btn-secondary w-full mt-6">Entendido</button>
      </Modal>
    </div>
    <GameGuide path="/snake" />
    </>
  );
}
